import { useState, useRef, useEffect } from "react";
import { Camera, Upload, Loader2, Package, Ruler, Box, CheckCircle, AlertCircle, X, Sparkles, IndianRupee, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIScannerSection() {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  // Fetch price estimate when scan result is available
  useEffect(() => {
    if (!result || !result.volume_cm3) return;
    axios.get(`${API}/api/pricing/estimate`, {
      params: { volume_cm3: result.volume_cm3 }
    }).then(({ data }) => setPriceEstimate(data)).catch(() => {});
  }, [result]);

  const handleFile = (file) => {
    if (!file) return;
    const valid = ["image/jpeg", "image/png", "image/webp"];
    if (!valid.includes(file.type)) {
      setError("Only JPG, PNG, or WEBP images are supported");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be under 10MB");
      return;
    }
    setError(null);
    setResult(null);
    setPreview(URL.createObjectURL(file));
    setImage(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const scan = async () => {
    if (!image) return;
    setScanning(true);
    setProgress(0);
    setError(null);
    setResult(null);

    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + Math.random() * 15, 90));
    }, 400);

    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(image);
      });

      const res = await axios.post(`${API}/api/scan-item`, {
        image_base64: base64,
      });

      setProgress(100);
      setTimeout(() => setResult(res.data), 300);
    } catch (err) {
      setError(err.response?.data?.detail || "Scanning failed. Try again.");
    } finally {
      clearInterval(interval);
      setScanning(false);
    }
  };

  const reset = () => {
    setImage(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setPriceEstimate(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const boxColors = {
    small: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "Small" },
    medium: { bg: "bg-[#FF6B00]/5", border: "border-[#FF6B00]/20", text: "text-[#FF6B00]", label: "Medium" },
    large: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", label: "Large" },
  };

  const confidenceColors = {
    High: "text-emerald-600 bg-emerald-50",
    Medium: "text-amber-600 bg-amber-50",
    Low: "text-red-600 bg-red-50",
  };

  return (
    <section id="ai-scanner" className="py-20 md:py-28" data-testid="ai-scanner-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] bg-orange-50 px-4 py-2 rounded-full mb-4 border border-orange-100">
            <Sparkles className="h-3.5 w-3.5" />
            AI Powered
          </span>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">
            Smart AI <span className="text-[#FF6B00]">Packing Assistant</span>
          </h2>
          <p className="text-base text-slate-600 leading-relaxed">
            Upload a photo of your item and our AI instantly detects its dimensions and recommends the perfect box size.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="ai-scanner-card">
            <div className="grid md:grid-cols-2">
              {/* Left: Upload Area */}
              <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-slate-100">
                {!preview ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div
                        className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-[#FF6B00]/40 hover:bg-orange-50/20 transition-all cursor-pointer group"
                        onClick={() => cameraRef.current?.click()}
                        data-testid="scanner-take-photo"
                      >
                        <div className="w-12 h-12 bg-[#FF6B00] rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-all">
                          <Camera className="h-6 w-6 text-white" />
                        </div>
                        <p className="text-sm font-semibold text-slate-700 mb-0.5">Take Photo</p>
                        <p className="text-[10px] text-slate-400">Open camera</p>
                      </div>
                      <div
                        className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-[#FF6B00]/40 hover:bg-orange-50/20 transition-all cursor-pointer group"
                        onClick={() => fileRef.current?.click()}
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        data-testid="scanner-upload-zone"
                      >
                        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-orange-100 group-hover:scale-110 transition-all">
                          <Camera className="h-6 w-6 text-[#FF6B00]" />
                        </div>
                        <p className="text-sm font-semibold text-slate-700 mb-0.5">Upload Photo</p>
                        <p className="text-[10px] text-slate-400">Browse gallery</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-400 text-center">JPG, PNG or WEBP (max 10MB) — Drag & drop also supported</p>
                  </div>
                ) : (
                  <div className="relative" data-testid="scanner-preview">
                    <div className="relative rounded-xl overflow-hidden bg-slate-50">
                      <img src={preview} alt="Item" className="w-full h-56 object-contain" />
                      {scanning && (
                        <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center">
                          {/* Corner brackets */}
                          <div className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-[#FF6B00] rounded-tl-lg animate-pulse" />
                          <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-[#FF6B00] rounded-tr-lg animate-pulse" />
                          <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-[#FF6B00] rounded-bl-lg animate-pulse" />
                          <div className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-[#FF6B00] rounded-br-lg animate-pulse" />
                          <div className="text-center">
                            <Loader2 className="h-8 w-8 text-[#FF6B00] animate-spin mx-auto mb-2" />
                            <p className="text-xs font-semibold text-white">Scanning...</p>
                          </div>
                        </div>
                      )}
                    </div>
                    {scanning && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Analyzing dimensions...</span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#FF6B00] rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {!scanning && (
                      <div className="flex gap-2 mt-3">
                        <Button
                          onClick={scan}
                          className="flex-1 bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-10 font-semibold text-sm shadow-sm shadow-[#FF6B00]/20"
                          data-testid="scanner-scan-btn"
                        >
                          <Sparkles className="h-4 w-4 mr-1.5" />
                          {result ? "Re-scan" : "Scan Item"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={reset}
                          className="rounded-xl h-10 border-slate-200 hover:bg-slate-50"
                          data-testid="scanner-reset-btn"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])}
                  data-testid="scanner-file-input"
                />
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])}
                  data-testid="scanner-camera-input"
                />
              </div>

              {/* Right: Results */}
              <div className="p-6 md:p-8 flex flex-col justify-center min-h-[280px]">
                {!result && !error && (
                  <div className="text-center text-slate-400" data-testid="scanner-placeholder">
                    <Box className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">Scan results will appear here</p>
                    <p className="text-xs mt-1">Upload & scan to get dimensions</p>
                  </div>
                )}

                {error && (
                  <div className="text-center" data-testid="scanner-error">
                    <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
                    <p className="text-sm text-red-600 font-medium">{error}</p>
                    <Button variant="outline" size="sm" onClick={reset} className="mt-3 rounded-xl text-xs">
                      Try Again
                    </Button>
                  </div>
                )}

                {result && (
                  <div className="space-y-3 animate-fadeInUp" data-testid="scanner-results">
                    {/* Item description */}
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span className="text-sm font-semibold text-slate-800">{result.description}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto ${confidenceColors[result.confidence] || confidenceColors.Medium}`}>
                        {result.confidence}
                      </span>
                    </div>

                    {/* Dimensions + Volume */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1.5">
                          <Ruler className="h-3.5 w-3.5 text-slate-500" />
                          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Dimensions</span>
                        </div>
                        {result.volume_cm3 > 0 && (
                          <span className="text-[10px] font-bold text-[#FF6B00] bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full" data-testid="scanner-volume">
                            Vol: {result.volume_cm3.toLocaleString()} cm³
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center">
                          <p className="text-lg font-bold text-slate-800">{result.estimated_length}</p>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">Length</p>
                        </div>
                        <div className="text-center border-x border-slate-200">
                          <p className="text-lg font-bold text-slate-800">{result.estimated_width}</p>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">Width</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-slate-800">{result.estimated_height}</p>
                          <p className="text-[10px] text-slate-400 uppercase font-medium">Height</p>
                        </div>
                      </div>
                    </div>

                    {/* Box recommendation */}
                    {(() => {
                      const bc = boxColors[result.box_size] || boxColors.medium;
                      return (
                        <div className={`${bc.bg} ${bc.border} border rounded-xl p-3 flex items-center gap-3`} data-testid="scanner-box-result">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bc.bg} border ${bc.border}`}>
                            <Package className={`h-4 w-4 ${bc.text}`} />
                          </div>
                          <div className="flex-1">
                            <p className={`text-xs font-bold ${bc.text}`}>{bc.label} Box Recommended</p>
                            <p className="text-[10px] text-slate-500 mt-0.5">{result.suggested_box}</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Volume-based Price Estimate */}
                    {priceEstimate && (
                      <div className="bg-[#0B132B] rounded-xl p-4 text-white" data-testid="scanner-price-estimate">
                        <div className="flex items-center gap-1.5 mb-2">
                          <TrendingUp className="h-3.5 w-3.5 text-[#FF6B00]" />
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Volume-Based Pricing</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <div>
                            <p className="text-xs text-slate-400">{priceEstimate.size_label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">Packing: <IndianRupee className="h-2.5 w-2.5 inline" />{priceEstimate.packing} + Delivery/km: <IndianRupee className="h-2.5 w-2.5 inline" />{Math.round(priceEstimate.delivery / priceEstimate.distance_km)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-slate-400">Estimated from</p>
                            <p className="text-xl font-bold text-[#FF6B00]"><IndianRupee className="h-4 w-4 inline" />{priceEstimate.total}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
